import { useEffect, useState } from 'react';
import './trade-view.styles.scss';
import { Link } from 'react-router-dom';
import { capYear, formatMoney, moneyRules } from '@shared/contracts.js';
import { MAX_OUTGOING, deadlineDay, nextDraftYear, ownedPicks, payrollAfter, pickLabel, tradeProblems, tradeWindowOpen } from '@shared/trades.js';
import { listenToDoc } from '@/utils/getDataFromDb';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { positions } from '@/utils/positions';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import HelpLink from '@/components/SHARED/help-link/help-link.component';
import { ModalAlert } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const EMPTY = { players: [], picks: [] };
const STATUS = { rejected: 'Declined', expired: 'Expired', withdrawn: 'Withdrawn', failed: 'Couldn’t go through' };

// Pro trades (summary/trades): offers to and from your team, a trade
// builder and the league's recent trades. myTeam: the manager's team
// document; without it (admins) the page is read-only.
const TradeView = ({ league, teams, teamLink, myTeam }) => {

    const [ trades, setTrades ] = useState(undefined);
    const [ draft, setDraft ] = useState(null);
    const [ partnerId, setPartnerId ] = useState('');
    const [ partner, setPartner ] = useState(null);
    const [ give, setGive ] = useState(EMPTY);
    const [ get, setGet ] = useState(EMPTY);
    const [ busy, setBusy ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const myId = myTeam?.id;

    useEffect(() => {
        const listeners = [];
        listenToDoc({ 'path': `leagues/${league.id}/summary/trades`, 'callback': setTrades, 'handleListener': (u) => listeners.push(u) });
        listenToDoc({ 'path': `leagues/${league.id}/summary/draft`, 'callback': setDraft, 'handleListener': (u) => listeners.push(u) });
        return () => listeners.forEach(u => u());
    }, [league.id])

    useEffect(() => {
        setPartner(null);
        setGet(EMPTY);
        if (!partnerId) return;
        let unsub;
        listenToDoc({ 'path': `leagues/${league.id}/teams/${partnerId}`, 'callback': setPartner, 'handleListener': (u) => { unsub = u; } });
        return () => { if (unsub) unsub(); };
    }, [league.id, partnerId])

    const rules = moneyRules(league.settings);
    if (!rules) return <div className='g-card'><p className='meta'>College leagues don&apos;t trade.</p></div>;
    if (trades === undefined) return <div className='center-col'><ThreeDotsLoader /></div>;

    const open = tradeWindowOpen(league);
    const year = capYear(league);
    const firstYear = nextDraftYear(league, draft);
    const owners = trades?.picks ?? {};
    const teamIds = Object.keys(teams ?? {});
    const picksOf = (id) => ownedPicks(id, teamIds, owners, firstYear);
    const tradable = teamIds.flatMap(picksOf);
    const offers = Object.values(trades?.offers ?? {}).filter(o => o.status === 'pending');
    const incoming = offers.filter(o => o.toTeamId === myId);
    const outgoing = offers.filter(o => o.fromTeamId === myId);
    const answers = (trades?.answers ?? []).filter(o => o.fromTeamId === myId).slice(0, 5);
    const team = (id) => teams?.[id];
    const abbr = (id) => team(id)?.abbr ?? id;

    const problems = myTeam && partner ? tradeProblems({ league, from: myTeam, to: partner, give, get, owners, tradable, rules }) : [];

    async function run(key, command, data, onDone) {
        setBusy(key);
        const res = await UseCloudFunction(command, { leagueId: league.id, ...data });
        setBusy(null);
        if (res.error) {
            setAlertMessage(res.error);
            return;
        }
        if (onDone) onDone(res);
    }

    function propose() {
        run('propose', 'proposeTrade', { toTeamId: partnerId, give, get }, (res) => {
            setAlertMessage(res.reply ? `${res.success} “${res.reply}”` : res.success);
            if (res.accepted || res.pending) {
                setGive(EMPTY);
                setGet(EMPTY);
            }
        });
    }

    const toggle = (side, setSide, kind, id) => {
        const list = side[kind];
        setSide({ ...side, [kind]: list.includes(id) ? list.filter(x => x !== id) : [...list, id] });
    };

    // One side of an offer, from its saved player briefs.
    const sideText = (offer, s) => [
        ...s.players.map(pid => {
            const b = offer.briefs?.[pid];
            return b ? `${b.fName} ${b.lName} (${positions[b.pos1 - 1]?.short}, ${b.overall}, ${formatMoney(b.salary)})` : 'a player';
        }),
        ...s.picks.map(id => pickLabel(id, teams)),
    ].join(', ') || 'nothing';

    const offerCard = (o, actions) => (
        <div key={o.id} className='g-card offer-card'>
            <div className='offer-teams'>
                <TeamBadge team={team(o.fromTeamId)} size='sm' />
                <span><Link to={teamLink(o.fromTeamId)}>{team(o.fromTeamId)?.name ?? o.fromTeamId}</Link> → <Link to={teamLink(o.toTeamId)}>{team(o.toTeamId)?.name ?? o.toTeamId}</Link></span>
            </div>
            <p><span className='meta'>{abbr(o.fromTeamId)} sends:</span> {sideText(o, o.give)}</p>
            <p><span className='meta'>{abbr(o.toTeamId)} sends:</span> {sideText(o, o.get)}</p>
            {o.reply && <p className='offer-reply'>“{o.reply}”</p>}
            {actions}
        </div>
    );

    return (
        <div className='trade-view'>
            <div className='g-card trade-head'>
                <h3>Trades <HelpLink section='trades' label='How trades work' /></h3>
                <p>{windowText(league, open)}</p>
                <p className='meta'>
                    Both teams must stay under the {formatMoney(rules.cap)} cap (or no further over it), and within {rules.rosterMin}–{rules.rosterMax} players during the season.
                    Draft picks for the next two drafts can be traded. Computer-run teams answer right away, judging players by ability, age and contract; a team going for the title wants help now, a rebuilding team wants youth and picks.
                </p>
            </div>

            {incoming.length > 0 &&
                <div className='offer-list'>
                    <h3 className='list-title'>Offers to you</h3>
                    {incoming.map(o => offerCard(o,
                        <div className='offer-actions'>
                            <button className={`g-button primary ${busy ? 'disabled' : ''}`} disabled={Boolean(busy)} onClick={() => run(`${o.id}-yes`, 'answerTrade', { offerId: o.id, accept: true }, (res) => setAlertMessage(res.success))}>
                                {busy === `${o.id}-yes` ? <ThreeDotsLoader /> : 'Accept'}
                            </button>
                            <button className={`g-button ${busy ? 'disabled' : ''}`} disabled={Boolean(busy)} onClick={() => run(`${o.id}-no`, 'answerTrade', { offerId: o.id, accept: false })}>
                                {busy === `${o.id}-no` ? <ThreeDotsLoader /> : 'Decline'}
                            </button>
                        </div>
                    ))}
                </div>
            }

            {outgoing.length > 0 &&
                <div className='offer-list'>
                    <h3 className='list-title'>Your offers ({outgoing.length} of {MAX_OUTGOING})</h3>
                    {outgoing.map(o => offerCard(o,
                        <div className='offer-actions'>
                            <span className='meta'>Waiting for an answer</span>
                            <button className={`g-button ${busy ? 'disabled' : ''}`} disabled={Boolean(busy)} onClick={() => run(`${o.id}-wd`, 'withdrawTrade', { offerId: o.id })}>
                                {busy === `${o.id}-wd` ? <ThreeDotsLoader /> : 'Withdraw'}
                            </button>
                        </div>
                    ))}
                </div>
            }

            {myTeam && open &&
                <div className='g-card trade-builder'>
                    <h3>Make a trade</h3>
                    <label className='partner-pick'>
                        <span className='meta'>Trade with</span>
                        <select value={partnerId} onChange={(e) => { setPartnerId(e.target.value); setGive(EMPTY); }}>
                            <option value=''>Choose a team</option>
                            {Object.values(teams ?? {}).filter(t => t.id !== myId).sort((a, b) => a.name.localeCompare(b.name)).map(t => (
                                <option key={t.id} value={t.id}>{t.name}{t.managerName ? ` (${t.managerName})` : ''}</option>
                            ))}
                        </select>
                    </label>
                    {partnerId && !partner && <ThreeDotsLoader />}
                    {partner &&
                        <>
                            <AssetList title={`${myTeam.abbr} sends`} team={myTeam} picks={picksOf(myId)} side={give} teams={teams} offseason={league.season.phase === 'offseason'}
                                onToggle={(kind, id) => toggle(give, setGive, kind, id)} />
                            <AssetList title={`${partner.abbr} sends`} team={partner} picks={picksOf(partner.id)} side={get} teams={teams} offseason={league.season.phase === 'offseason'}
                                onToggle={(kind, id) => toggle(get, setGet, kind, id)} />
                            <dl className='money-list'>
                                <dt>{myTeam.abbr} payroll after</dt>
                                <dd>{formatMoney(payrollAfter(myTeam, partner, give.players, get.players, year))} · {Object.keys(myTeam.players).length - give.players.length + get.players.length} players</dd>
                                <dt>{partner.abbr} payroll after</dt>
                                <dd>{formatMoney(payrollAfter(partner, myTeam, get.players, give.players, year))} · {Object.keys(partner.players).length - get.players.length + give.players.length} players</dd>
                            </dl>
                            {problems.length > 0 && (give.players.length + give.picks.length + get.players.length + get.picks.length > 0) &&
                                <ul className='trade-problems'>{problems.map(p => <li key={p}>{p}</li>)}</ul>
                            }
                            <button className={`g-button primary propose-btn ${busy || problems.length ? 'disabled' : ''}`} disabled={Boolean(busy) || problems.length > 0} onClick={propose}>
                                {busy === 'propose' ? <ThreeDotsLoader /> : partner.managerId ? `Send offer to ${partner.managerName ?? 'their manager'}` : 'Offer trade'}
                            </button>
                        </>
                    }
                </div>
            }

            {answers.length > 0 &&
                <div className='g-card'>
                    <h3>Answers to your offers</h3>
                    <ul className='answer-list'>
                        {answers.map(o => (
                            <li key={`${o.id}-${o.status}`}>
                                <b>{abbr(o.toTeamId)}</b>: {STATUS[o.status] ?? o.status}{o.reply ? <> · “{o.reply}”</> : ''}
                                <div className='meta'>You offered {sideText(o, o.give)} for {sideText(o, o.get)}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            }

            <div className='g-card'>
                <h3>Recent trades</h3>
                {(trades?.log ?? []).length === 0 ? <p className='meta'>No trades yet.</p> :
                    <ul className='trade-log'>
                        {trades.log.slice(0, 25).map(o => (
                            <li key={o.id} className={o.fromTeamId === myId || o.toTeamId === myId ? 'mine' : ''}>
                                <div className='meta'>{clockText(o.clockDone ?? o.clock)}</div>
                                <div><b>{abbr(o.fromTeamId)}</b> gets {sideText(o, o.get)}</div>
                                <div><b>{abbr(o.toTeamId)}</b> gets {sideText(o, o.give)}</div>
                            </li>
                        ))}
                    </ul>
                }
            </div>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

// Checkboxes for one team's players and picks.
const AssetList = ({ title, team, picks, side, teams, offseason, onToggle }) => {
    const players = Object.values(team.players).sort((a, b) => b.overall - a.overall);
    return (
        <fieldset className='asset-list'>
            <legend>{title}</legend>
            {players.map(p => {
                const expiring = offseason && p.contract?.years === 0;
                return (
                    <label key={p.id} className={`asset ${expiring ? 'off' : ''} ${side.players.includes(p.id) ? 'picked' : ''}`}>
                        <input type='checkbox' checked={side.players.includes(p.id)} disabled={expiring} onChange={() => onToggle('players', p.id)} />
                        <span className='asset-name'><b>{p.fName} {p.lName}</b> <span className='meta'>{positions[p.pos1 - 1]?.short} · {p.age} · {p.overall}</span></span>
                        <span className='asset-money'>{p.contract ? (expiring ? 'Expiring' : `${formatMoney(p.contract.salary)} × ${p.contract.years}`) : ''}</span>
                    </label>
                );
            })}
            {picks.map(id => (
                <label key={id} className={`asset ${side.picks.includes(id) ? 'picked' : ''}`}>
                    <input type='checkbox' checked={side.picks.includes(id)} onChange={() => onToggle('picks', id)} />
                    <span className='asset-name'>{pickLabel(id, teams)}</span>
                </label>
            ))}
        </fieldset>
    );
}

function windowText(league, open) {
    const s = league.season;
    if (open) {
        if (s.phase === 'regular' || s.phase === 'preseason') return `Trading is open until the deadline, after day ${deadlineDay(league) - 1} of the regular season.`;
        return 'Trading is open during free agency.';
    }
    if (s.phase === 'offseason') return 'Trading reopens when free agency opens, after the draft.';
    return 'The trade deadline has passed. Trading reopens when free agency opens.';
}

function clockText(clock) {
    const year = Math.floor(clock / 1000);
    const rest = clock % 1000;
    return rest >= 500 ? `Season ${year} offseason` : `Season ${year} · Day ${rest}`;
}

export default TradeView
